import { useEffect, useRef, useMemo } from 'react';
import useContentAnalysisProgress from './useContentAnalysisProgress';
import { useLazyFetchDomainContentAnalysisQuery } from '../../service';
import { DomainContentAnalysis, WebsiteProfile, ContentEffectiveness, CompetitorContentAnalysis, ContentTrustAnalysis } from '../../types';


type DomainContentAnalysisHook = {
    data: DomainContentAnalysis | undefined;
    isInProgress: boolean;
    isLoading: boolean;
    isFetchingLatestAnalysis: boolean;
    error: unknown;
    activeProcess: {
        websiteProfile: WebsiteProfile | null;
        contentEffectiveness: ContentEffectiveness | null;
        competitorContent: CompetitorContentAnalysis | null;
        contentTrust: ContentTrustAnalysis | null;
    };
};

/**
 * Orchestrates domain content analysis lifecycle:
 * - If an analysis process is active, we let the progress hook poll and expose loading
 * - If no active process, that means at least one analysis has completed. In this case, we are fetching the latest analysis
 */
export default function useDomainContentAnalysis(domain: string): DomainContentAnalysisHook {
    const { hasActiveProcess, isLoading: isLoadingActiveProcess, activeProcess } = useContentAnalysisProgress(domain);
    const [fetchLatestAnalysis, fetchLatestAnalysisState] = useLazyFetchDomainContentAnalysisQuery();

    // Prevent duplicate orchestration while conditions remain stable
    const attemptedRef = useRef(false);
    const inFlightFetchRef = useRef<ReturnType<typeof fetchLatestAnalysis> | null>(null);
    const isUnmountedRef = useRef(false);

    useEffect(() => {
        return () => {
            isUnmountedRef.current = true;
        };
    }, []);

    useEffect(() => {
        // Only orchestrate when not actively analyzing and not loading the active-process status
        if (hasActiveProcess || isLoadingActiveProcess) return;

        // Avoid repeated triggers while state is stable
        if (attemptedRef.current) return;
        if (fetchLatestAnalysisState.isFetching) return;
        if (activeProcess.websiteProfile || activeProcess.contentEffectiveness || activeProcess.competitorContent || activeProcess.contentTrust) return;

        attemptedRef.current = true;
        const latestAnalysisPromise = fetchLatestAnalysis({ domain });
        inFlightFetchRef.current = latestAnalysisPromise;

        latestAnalysisPromise.unwrap()
            .catch(() => {
                return Promise.resolve();
            })
            .finally(() => {
                inFlightFetchRef.current = null;
            });

        // Cleanup: abort in-flight fetch only if unmounting
        return () => {
            if (isUnmountedRef.current) {
                try {
                    inFlightFetchRef.current?.abort?.();
                } catch (_) { }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, hasActiveProcess, isLoadingActiveProcess, fetchLatestAnalysis]);

    // Reset the attempted flag when conditions change (e.g., active process starts/stops)
    useEffect(() => {
        if (hasActiveProcess || isLoadingActiveProcess) {
            attemptedRef.current = false;
        }
    }, [hasActiveProcess, isLoadingActiveProcess]);

    const isFetchingLatestAnalysis = !!fetchLatestAnalysisState.isFetching;
    const isLoading = !!(isLoadingActiveProcess || isFetchingLatestAnalysis);

    const non404Error = useMemo(() => {
        const e: any = fetchLatestAnalysisState.error;
        if (!e) return undefined;
        const status = (e && typeof e === 'object' && 'status' in e) ? e.status : e?.originalStatus;
        return status === 404 ? undefined : e;
    }, [fetchLatestAnalysisState.error]);

    return {
        data: fetchLatestAnalysisState.data,
        isInProgress: hasActiveProcess,
        isLoading,
        isFetchingLatestAnalysis,
        error: non404Error,
        activeProcess: activeProcess
    };
}